import { forceSimulation, forceX, forceY, type Simulation } from 'd3-force'
import type { BubblePhysicsState, FlowNode, VisualizationConfig } from '../types/market'
import { clamp, lerp, mulberry32 } from '../utils/math'
import {
  createDynamicCollisionForce,
  PHYSICS_RADIUS_SCALE,
  type DynamicCollisionForce,
} from './DynamicCollisionForce'

type DirectionSign = 1 | -1

export class FundsForceField {
  readonly zeroAxisY = 925
  private simulation: Simulation<FlowNode, undefined>
  private readonly collisionForce: DynamicCollisionForce
  private readonly lastReheatFlows = new Map<string, number>()
  private readonly previousPositions: Float64Array
  private currentTimelineMs = 0
  private readonly xForce = forceX<FlowNode>((node) => this.targetX(node))
    .strength((node) => this.xStrength(node))
  private readonly yForce = forceY<FlowNode>((node) => node.targetY)
    .strength((node) => this.yStrength(node))

  get alpha(): number {
    return this.simulation.alpha()
  }

  constructor(
    private readonly nodes: FlowNode[],
    private readonly config: VisualizationConfig,
  ) {
    this.previousPositions = new Float64Array(nodes.length * 2)
    for (const node of nodes) {
      this.initializeNodeState(node)
      this.lastReheatFlows.set(node.id, node.flow)
    }
    this.collisionForce = createDynamicCollisionForce(config, 1)
    this.simulation = forceSimulation(nodes)
      .randomSource(mulberry32(20260831))
      .alphaDecay(0.026)
      .alphaTarget(0)
      .velocityDecay(0.52)
      .force('x', this.xForce)
      .force('y', this.yForce)
      .force('collide', this.collisionForce)
      .stop()
    this.updateForces(true, 0)
  }

  updateForces(reheat = false, timelineMs = this.currentTimelineMs): void {
    this.currentTimelineMs = timelineMs
    for (const node of this.nodes) {
      this.updatePhysicsState(node, timelineMs)
      node.targetY = this.calculateTargetY(node)
      node.collisionRadius = this.collisionForce.effectiveRadius(node)
    }
    this.xForce.x((node) => this.targetX(node)).strength((node) => this.xStrength(node))
    this.yForce.y((node) => node.targetY).strength((node) => this.yStrength(node))
    this.simulation.alphaTarget(0)
    if (reheat) {
      const changed = this.nodes.some((node) =>
        Math.abs(node.flow - (this.lastReheatFlows.get(node.id) ?? node.flow)) > 1_000_000)
      for (const node of this.nodes) this.lastReheatFlows.set(node.id, node.flow)
      if (changed) this.simulation.alpha(Math.max(this.simulation.alpha(), 0.18))
    }
  }

  tick(iterations = 1, timelineMs = this.currentTimelineMs): void {
    if (timelineMs !== this.currentTimelineMs) this.updateForces(false, timelineMs)
    for (let iteration = 0; iteration < iterations; iteration += 1) {
      for (let index = 0; index < this.nodes.length; index += 1) {
        this.previousPositions[index * 2] = this.nodes[index].x ?? 0
        this.previousPositions[index * 2 + 1] = this.nodes[index].y ?? 0
      }
      this.simulation.tick(1)
      this.limitCrossingStep(this.previousPositions)
      this.constrainCanvasBounds()
      for (const node of this.nodes) this.afterTick(node, timelineMs)
      for (let projection = 0; projection < this.config.stableProjectionIterations; projection += 1) {
        if (!this.projectStableCollisions(1)) break
      }
      this.limitFinalFrameStep(this.previousPositions)
      this.constrainCanvasBounds()
      this.enforceStableRegions()
      this.finalizeArrivedCrossings()
      this.settleStableVelocities()
    }
  }

  stabilize(iterations = 320): void {
    this.simulation.alpha(1)
    for (let iteration = 0; iteration < iterations; iteration += 1) {
      this.updateForces(false, this.currentTimelineMs)
      this.tick(1, this.currentTimelineMs)
    }
    // Finish initialization with a position-only solve. This resolves dense
    // reset layouts without storing separation energy in vx/vy.
    this.simulation.alpha(0).alphaTarget(0)
    for (const node of this.nodes) {
      node.vx = 0
      node.vy = 0
    }
    for (let iteration = 0; iteration < this.config.stableProjectionIterations * 5; iteration += 1) {
      const corrected = this.projectStableCollisions(1)
      this.constrainCanvasBounds()
      this.enforceStableRegions()
      if (!corrected) break
    }
    for (const node of this.nodes) {
      node.vx = 0
      node.vy = 0
    }
  }

  reset(): void {
    for (const node of this.nodes) {
      const sign = this.signOf(node.flow, this.signOf(node.targetFlow, 1))
      const unsignedSeed = node.seed >>> 0
      const column = unsignedSeed % 7
      const row = Math.floor(unsignedSeed / 7) % 5
      node.x = this.config.safeArea.left + 95 + column * 132 + (unsignedSeed % 29)
      node.y = sign > 0 ? 430 + row * 84 : 1050 + row * 86
      node.vx = 0
      node.vy = 0
      node.currentSign = sign
      node.targetSign = sign
      node.physicsState = sign > 0 ? 'STABLE_POSITIVE' : 'STABLE_NEGATIVE'
      node.isCrossing = false
      node.crossingArrived = false
      node.crossingProgress = 1
      node.crossingStartedAt = -Infinity
      node.crossingStartY = node.y
      node.crossingLaneOffset = this.laneOffset(node)
      node.collisionScale = 1
      node.polarity = sign
      node.targetY = this.calculateTargetY(node)
    }
    this.currentTimelineMs = 0
    for (const node of this.nodes) this.lastReheatFlows.set(node.id, node.flow)
    this.simulation.alphaTarget(0)
    this.simulation.alpha(1)
    this.stabilize(320)
  }

  destroy(): void {
    this.simulation.stop()
  }

  isInCorrectRegion(node: FlowNode, sign = node.targetSign): boolean {
    const y = node.y ?? this.zeroAxisY
    return sign > 0
      ? y <= this.positiveBoundary(node)
      : y >= this.negativeBoundary(node)
  }

  private initializeNodeState(node: FlowNode): void {
    const sign = this.signOf(node.flow, 1)
    node.currentSign = sign
    node.targetSign = this.signOf(node.targetFlow, sign)
    node.physicsState = sign > 0 ? 'STABLE_POSITIVE' : 'STABLE_NEGATIVE'
    node.isCrossing = false
    node.crossingArrived = false
    node.crossingProgress = 1
    node.crossingStartedAt = -Infinity
    node.crossingStartY = node.y ?? this.zeroAxisY
    node.crossingLaneOffset = this.laneOffset(node)
    node.collisionScale = 1
    node.collisionRadius = node.radius * PHYSICS_RADIUS_SCALE + this.config.collisionPadding
    node.targetY = this.calculateTargetY(node)
    node.polarity = sign
  }

  private updatePhysicsState(node: FlowNode, now: number): void {
    const desiredSign = this.signOf(node.targetSign, this.signOf(node.targetFlow, node.currentSign))
    node.targetSign = desiredSign
    const crossingToward = node.physicsState === 'CROSSING_TO_POSITIVE' ? 1
      : node.physicsState === 'CROSSING_TO_NEGATIVE' ? -1
        : 0
    const physicallySafe = this.isInCorrectRegion(node, desiredSign)

    if (!node.isCrossing && (desiredSign !== node.currentSign || !physicallySafe)) {
      this.beginCrossing(node, desiredSign, now)
    } else if (node.isCrossing && crossingToward !== desiredSign) {
      this.beginCrossing(node, desiredSign, now)
    }

    if (node.crossingArrived) {
      // Reclaim the full solid radius in roughly 5 rendered frames so a
      // completed crossing cannot linger visually in a half-collidable state.
      node.collisionScale = lerp(node.collisionScale, 1, 0.6)
      if (node.collisionScale > 0.992) node.collisionScale = 1
    } else if (node.isCrossing) {
      const elapsed = Math.max(0, now - node.crossingStartedAt)
      const overdue = clamp(elapsed / this.config.maxCrossingDuration - 1, 0, 1)
      const targetScale = lerp(this.config.crossingCollisionScale, 0.24, overdue)
      node.collisionScale = lerp(node.collisionScale, targetScale, 0.32)
    } else {
      node.collisionScale = lerp(node.collisionScale, 1, 0.12)
      if (node.collisionScale > 0.992) node.collisionScale = 1
    }
  }

  private beginCrossing(node: FlowNode, targetSign: DirectionSign, now: number): void {
    const physicalSign: DirectionSign = (node.y ?? this.zeroAxisY) <= this.zeroAxisY ? 1 : -1
    node.currentSign = physicalSign
    node.targetSign = targetSign
    node.physicsState = targetSign > 0 ? 'CROSSING_TO_POSITIVE' : 'CROSSING_TO_NEGATIVE'
    node.isCrossing = true
    node.crossingArrived = false
    node.crossingProgress = 0
    node.crossingStartedAt = now
    node.crossingStartY = node.y ?? this.zeroAxisY
    node.crossingLaneOffset = this.laneOffset(node)
    node.collisionScale = Math.min(node.collisionScale, this.config.crossingCollisionScale)
    // One bounded crossing impulse is enough; updateForces must not keep the
    // entire simulation hot on every rendered frame.
    this.simulation.alpha(Math.max(this.simulation.alpha(), 0.56))
  }

  private finishCollisionRecovery(node: FlowNode): void {
    node.currentSign = node.targetSign
    node.physicsState = node.targetSign > 0 ? 'STABLE_POSITIVE' : 'STABLE_NEGATIVE'
    node.isCrossing = false
    node.crossingArrived = false
    node.crossingProgress = 1
    node.polarity = node.targetSign
    node.vx = (node.vx ?? 0) * 0.48
    node.vy = (node.vy ?? 0) * 0.28
    node.collisionScale = 1
  }

  private afterTick(node: FlowNode, now: number): void {
    if (node.isCrossing && !node.crossingArrived) {
      const y = node.y ?? this.zeroAxisY
      const boundary = node.targetSign > 0 ? this.positiveBoundary(node) : this.negativeBoundary(node)
      const totalDistance = Math.max(1, Math.abs(boundary - node.crossingStartY))
      const travelled = node.targetSign > 0
        ? node.crossingStartY - y
        : y - node.crossingStartY
      node.crossingProgress = clamp(travelled / totalDistance, 0, 1)

      const elapsed = Math.max(0, now - node.crossingStartedAt)
      const direction = node.targetSign > 0 ? -1 : 1
      const minimumVelocity = elapsed > this.config.maxCrossingDuration ? 2.8 : 0.7
      node.vy = direction < 0
        ? Math.min(node.vy ?? 0, -minimumVelocity)
        : Math.max(node.vy ?? 0, minimumVelocity)

      if (elapsed > this.config.maxCrossingDuration) {
        // Continuous fail-safe: no teleport, but no collision equilibrium can survive it.
        const overdue = clamp(elapsed / this.config.maxCrossingDuration - 1, 0, 1.5)
        const step = clamp((node.targetY - y) * (0.035 + overdue * 0.035), -9, 9)
        node.y = y + step
        node.collisionScale = lerp(node.collisionScale, 0.24, 0.38)
      }

      this.updateCrossingColor(node)
      if (this.isInCorrectRegion(node)) this.arriveAtTargetRegion(node)
    } else {
      // Stable nodes are never permitted to remain on the wrong side.
      if (!this.isInCorrectRegion(node)) {
        this.beginCrossing(node, node.targetSign, now)
      } else {
        node.polarity = node.targetSign
      }
    }
    node.collisionRadius = this.collisionForce.effectiveRadius(node)
  }

  private arriveAtTargetRegion(node: FlowNode): void {
    node.physicsState = stableStateForSign(node.targetSign)
    node.isCrossing = false
    node.crossingArrived = true
    node.crossingProgress = 1
    node.currentSign = node.targetSign
    node.polarity = node.targetSign
    node.vx = (node.vx ?? 0) * 0.48
    node.vy = (node.vy ?? 0) * 0.28
  }

  private updateCrossingColor(node: FlowNode): void {
    const y = node.y ?? this.zeroAxisY
    // The neutral portion is intentionally narrow to retain the existing red/green style.
    const colorBand = clamp(node.radius * 0.34, 24, 42)
    node.polarity = clamp((this.zeroAxisY - y) / colorBand, -1, 1)
  }

  private xStrength(node: FlowNode): number {
    if (node.crossingArrived) return 0.026
    if (!node.isCrossing) return 0.018 + Math.abs(node.polarity) * 0.01
    const distanceToAxis = Math.abs((node.y ?? this.zeroAxisY) - this.zeroAxisY)
    return distanceToAxis <= this.config.crossingZoneHalfHeight ? 0.009 : 0.018
  }

  private yStrength(node: FlowNode): number {
    if (node.crossingArrived) return 0.14
    if (!node.isCrossing) return 0.04 + Math.abs(node.polarity) * 0.025
    const elapsed = Math.max(0, this.currentTimelineMs - node.crossingStartedAt)
    const overdue = clamp(elapsed / this.config.maxCrossingDuration - 1, 0, 1)
    const inCrossingZone = Math.abs((node.y ?? this.zeroAxisY) - this.zeroAxisY) <= this.config.crossingZoneHalfHeight
    return clamp(this.config.crossingForceStrength + (inCrossingZone ? 0.12 : 0) + overdue * 0.22, 0.3, 0.9)
  }

  private targetX(node: FlowNode): number {
    const usableLeft = this.config.safeArea.left + node.radius + 14
    const usableRight = this.config.videoWidth - this.config.safeArea.right - node.radius - 14
    const hashed = ((node.seed * 2654435761) >>> 0) / 4294967295
    const magnitude = clamp(Math.abs(node.flow) / 11_000_000_000, 0, 1)
    const distributed = usableLeft + hashed * (usableRight - usableLeft)
    const base = distributed * (1 - magnitude * 0.34) + this.config.videoWidth * 0.5 * magnitude * 0.34
    if (!node.isCrossing) return base
    const zone = 1 - clamp(Math.abs((node.y ?? this.zeroAxisY) - this.zeroAxisY) / this.config.crossingZoneHalfHeight, 0, 1)
    return clamp(base + node.crossingLaneOffset * (0.35 + zone * 0.65), usableLeft, usableRight)
  }

  private calculateTargetY(node: FlowNode): number {
    const magnitude = clamp(Math.log1p(Math.abs(node.flow) / 100_000_000) / Math.log1p(130), 0, 1)
    const jitter = ((node.seed % 101) / 100 - 0.5) * 105
    const radiusWeight = magnitude * 22 + node.radius * 0.08
    if (node.targetSign > 0) {
      const natural = this.zeroAxisY - 136 - (1 - magnitude) * 330 + jitter * (1 - magnitude * 0.5) - radiusWeight
      return Math.min(natural, this.positiveBoundary(node) - 8)
    }
    const natural = this.zeroAxisY + 142 + (1 - magnitude) * 360 + jitter * (1 - magnitude * 0.5) + radiusWeight
    return Math.max(natural, this.negativeBoundary(node) + 8)
  }

  private positiveBoundary(node: FlowNode): number {
    return this.zeroAxisY - node.radius - this.config.axisGap
  }

  private negativeBoundary(node: FlowNode): number {
    return this.zeroAxisY + node.radius + this.config.axisGap
  }

  private constrainCanvasBounds(): void {
    const top = 365
    const bottom = 1475
    for (const node of this.nodes) {
      if (node.x == null || node.y == null) continue
      const left = this.config.safeArea.left + node.radius
      const right = this.config.videoWidth - this.config.safeArea.right - node.radius
      const upper = top + node.radius
      const lower = bottom - node.radius
      if (node.x < left) {
        node.x = left
        node.vx = Math.abs(node.vx ?? 0) * 0.25
      } else if (node.x > right) {
        node.x = right
        node.vx = -Math.abs(node.vx ?? 0) * 0.25
      }
      if (node.y < upper) {
        node.y = upper
        node.vy = Math.abs(node.vy ?? 0) * 0.22
      } else if (node.y > lower) {
        node.y = lower
        node.vy = -Math.abs(node.vy ?? 0) * 0.22
      }
    }
  }

  private limitCrossingStep(previousPositions: Float64Array): void {
    for (let index = 0; index < this.nodes.length; index += 1) {
      const node = this.nodes[index]
      if ((!node.isCrossing && node.collisionScale >= 0.98) || node.x == null || node.y == null) continue
      const previousX = previousPositions[index * 2]
      const previousY = previousPositions[index * 2 + 1]
      const maxYStep = this.config.crossingMaxStep
      const maxXStep = maxYStep * 0.72
      const deltaY = node.y - previousY
      const deltaX = node.x - previousX
      if (Math.abs(deltaY) > maxYStep) {
        node.y = previousY + Math.sign(deltaY) * maxYStep
        node.vy = clamp(node.vy ?? 0, -maxYStep, maxYStep) * 0.72
      }
      if (Math.abs(deltaX) > maxXStep) {
        node.x = previousX + Math.sign(deltaX) * maxXStep
        node.vx = clamp(node.vx ?? 0, -maxXStep, maxXStep) * 0.72
      }
    }
  }

  private limitFinalFrameStep(previousPositions: Float64Array): void {
    for (let index = 0; index < this.nodes.length; index += 1) {
      const node = this.nodes[index]
      if (node.x == null || node.y == null) continue
      const previousX = previousPositions[index * 2]
      const previousY = previousPositions[index * 2 + 1]
      const maxYStep = this.config.crossingMaxStep
      const maxXStep = maxYStep * 0.72
      node.y = previousY + clamp(node.y - previousY, -maxYStep, maxYStep)
      node.x = previousX + clamp(node.x - previousX, -maxXStep, maxXStep)
      node.vy = clamp(node.vy ?? 0, -maxYStep, maxYStep)
      node.vx = clamp(node.vx ?? 0, -maxXStep, maxXStep)
    }
  }

  private hasCollisionOverlap(node: FlowNode): boolean {
    if (node.x == null || node.y == null) return true
    const nodeX = node.x
    const nodeY = node.y
    const radius = node.collisionRadius
    // collisionPadding is an invisible buffer, so a sub-pixel-sized share of
    // it must not keep an otherwise completed crossing state alive forever.
    const recoveryTolerance = Math.max(0.5, this.config.collisionPadding * 0.25)
    return this.nodes.some((other) => {
      if (other === node || other.targetSign !== node.targetSign || other.x == null || other.y == null) return false
      const distance = Math.hypot(other.x - nodeX, other.y - nodeY)
      return distance < radius + other.collisionRadius - recoveryTolerance
    })
  }

  private projectStableCollisions(iterations: number): boolean {
    let corrected = false
    for (let iteration = 0; iteration < iterations; iteration += 1) {
      for (let leftIndex = 0; leftIndex < this.nodes.length; leftIndex += 1) {
        const left = this.nodes[leftIndex]
        if (!this.isProjectionEligible(left) || left.x == null || left.y == null) continue
        for (let rightIndex = leftIndex + 1; rightIndex < this.nodes.length; rightIndex += 1) {
          const right = this.nodes[rightIndex]
          if (
            !this.isProjectionEligible(right)
            || right.targetSign !== left.targetSign
            || right.x == null
            || right.y == null
          ) continue
          let dx = right.x - left.x
          let dy = right.y - left.y
          const minimumDistance = left.collisionRadius + right.collisionRadius
          // Most pairs are far apart. Reject them before boundary math and
          // sqrt while preserving the exact solver and pair order for every
          // pair that can actually overlap.
          if (Math.abs(dx) >= minimumDistance || Math.abs(dy) >= minimumDistance) continue
          const leftAxisBoundary = left.targetSign > 0
            ? this.zeroAxisY - left.radius - this.config.axisGap
            : this.zeroAxisY + left.radius + this.config.axisGap
          const rightAxisBoundary = right.targetSign > 0
            ? this.zeroAxisY - right.radius - this.config.axisGap
            : this.zeroAxisY + right.radius + this.config.axisGap
          const bothOnAxisBoundary = left.targetSign > 0
            ? left.y >= leftAxisBoundary - 0.75 && right.y >= rightAxisBoundary - 0.75
            : left.y <= leftAxisBoundary + 0.75 && right.y <= rightAxisBoundary + 0.75
          if (bothOnAxisBoundary) {
            // Two bubbles constrained against the same side of the zero axis
            // cannot resolve a downward/upward component. Separate them only
            // along the free tangent instead of accumulating blocked velocity.
            if (Math.abs(dx) < 1e-4) dx = left.seed < right.seed ? 1 : -1
            dy = 0
          }
          let distance = Math.hypot(dx, dy)
          if (distance >= minimumDistance - 0.05) continue
          corrected = true
          if (distance < 1e-4) {
            const angle = ((left.seed ^ right.seed) % 360) * Math.PI / 180
            dx = Math.cos(angle)
            dy = Math.sin(angle)
            distance = 1
          }
          const overlap = minimumDistance - distance
          const correction = Math.min(10, overlap * 0.58)
          const unitX = dx / distance
          const unitY = dy / distance
          const leftMass = Math.max(1, left.radius * left.radius)
          const rightMass = Math.max(1, right.radius * right.radius)
          const leftShare = left.crossingArrived !== right.crossingArrived
            ? left.crossingArrived ? 0.18 : 0.82
            : rightMass / (leftMass + rightMass)
          const rightShare = left.crossingArrived !== right.crossingArrived
            ? right.crossingArrived ? 0.18 : 0.82
            : leftMass / (leftMass + rightMass)
          let resolved = this.moveProjectedNode(left, -unitX, -unitY, correction * leftShare)
          resolved += this.moveProjectedNode(right, unitX, unitY, correction * rightShare)

          // If a wall or the zero-axis boundary consumed one side's motion,
          // transfer that unresolved correction to the particle that still has
          // inward room. This prevents dense packs from pinning two balls into
          // the same canvas corner.
          let remaining = Math.max(0, correction - resolved)
          const leftFirst = right.crossingArrived || (!left.crossingArrived && !right.crossingArrived)
          if (leftFirst) {
            remaining -= this.moveProjectedNode(left, -unitX, -unitY, remaining)
            if (remaining > 0.001) this.moveProjectedNode(right, unitX, unitY, remaining)
          } else {
            remaining -= this.moveProjectedNode(right, unitX, unitY, remaining)
            if (remaining > 0.001) this.moveProjectedNode(left, -unitX, -unitY, remaining)
          }
          if (overlap > 1) {
            const tangentSign = ((left.seed ^ right.seed) & 1) === 0 ? 1 : -1
            const tangentX = -unitY * tangentSign
            const tangentY = unitX * tangentSign
            const slide = Math.min(1, overlap * 0.04)
            this.moveProjectedNode(left, -tangentX, -tangentY, slide * leftShare)
            this.moveProjectedNode(right, tangentX, tangentY, slide * rightShare)
          }
          this.removeClosingVelocity(left, right, unitX, unitY, leftShare, rightShare)
        }
      }
    }
    return corrected
  }

  private moveProjectedNode(
    node: FlowNode,
    directionX: number,
    directionY: number,
    distance: number,
  ): number {
    if (distance <= 0 || node.x == null || node.y == null) return 0
    const oldX = node.x
    const oldY = node.y
    const minX = this.config.safeArea.left + node.radius
    const maxX = this.config.videoWidth - this.config.safeArea.right - node.radius
    let minY = 365 + node.radius
    let maxY = 1475 - node.radius
    if (node.targetSign > 0) maxY = Math.min(maxY, this.zeroAxisY - node.radius - this.config.axisGap)
    else minY = Math.max(minY, this.zeroAxisY + node.radius + this.config.axisGap)
    node.x = clamp(oldX + directionX * distance, minX, maxX)
    node.y = clamp(oldY + directionY * distance, minY, maxY)
    return Math.max(0, (node.x - oldX) * directionX + (node.y - oldY) * directionY)
  }

  private enforceStableRegions(): void {
    for (const node of this.nodes) {
      if ((node.isCrossing && !node.crossingArrived) || node.y == null) continue
      if (node.targetSign > 0 && node.y > this.positiveBoundary(node)) {
        node.y = this.positiveBoundary(node)
        node.vy = Math.min(node.vy ?? 0, 0)
      } else if (node.targetSign < 0 && node.y < this.negativeBoundary(node)) {
        node.y = this.negativeBoundary(node)
        node.vy = Math.max(node.vy ?? 0, 0)
      }
    }
  }

  private isProjectionEligible(node: FlowNode): boolean {
    return (!node.isCrossing || node.crossingArrived) && node.collisionScale >= 0.999
  }

  private finalizeArrivedCrossings(): void {
    for (const node of this.nodes) {
      if (
        !node.isCrossing
        && node.crossingArrived
        && node.collisionScale >= 0.992
        && !this.hasCollisionOverlap(node)
      ) {
        this.finishCollisionRecovery(node)
      }
      node.collisionRadius = this.collisionForce.effectiveRadius(node)
    }
  }

  private removeClosingVelocity(
    left: FlowNode,
    right: FlowNode,
    unitX: number,
    unitY: number,
    leftShare: number,
    rightShare: number,
  ): void {
    const relativeNormalVelocity = ((right.vx ?? 0) - (left.vx ?? 0)) * unitX
      + ((right.vy ?? 0) - (left.vy ?? 0)) * unitY
    if (relativeNormalVelocity >= 0) return
    const closingSpeed = -relativeNormalVelocity
    left.vx = (left.vx ?? 0) - unitX * closingSpeed * leftShare
    left.vy = (left.vy ?? 0) - unitY * closingSpeed * leftShare
    right.vx = (right.vx ?? 0) + unitX * closingSpeed * rightShare
    right.vy = (right.vy ?? 0) + unitY * closingSpeed * rightShare
  }

  private settleStableVelocities(): void {
    for (const node of this.nodes) {
      if (node.isCrossing) continue
      const threshold = node.crossingArrived ? 0.012 : 0.025
      if (Math.abs(node.vx ?? 0) < threshold) node.vx = 0
      if (Math.abs(node.vy ?? 0) < threshold) node.vy = 0
    }
  }

  private laneOffset(node: FlowNode): number {
    return (((node.seed >>> 0) % 5) - 2) * 38
  }

  private signOf(value: number, fallback: DirectionSign): DirectionSign {
    return value < 0 ? -1 : value > 0 ? 1 : fallback
  }
}

export function stableStateForSign(sign: DirectionSign): BubblePhysicsState {
  return sign > 0 ? 'STABLE_POSITIVE' : 'STABLE_NEGATIVE'
}
